import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useStations } from "@/hooks/useStations";
import { userApi } from "@/lib/api/services/user.api";
import type { AssigneeFilter } from "./assignee-filter";

interface AssigneeFilterOptionsProps {
  locationId?: number;
  value: AssigneeFilter;
  onChange: (next: AssigneeFilter) => void;
}

/**
 * The Station and Chef sections of a lane's Filter menu. Rendered inside the
 * open menu, so neither list is fetched until someone opens it.
 */
export function AssigneeFilterOptions({ locationId, value, onChange }: AssigneeFilterOptionsProps) {
  const { t } = useLingui();
  const { data: stations } = useStations({ locationId }, locationId !== undefined);
  const { data: chefs } = useQuery({
    queryKey: ["users", "assignee-filter", "CHEF"],
    queryFn: () => userApi.paginate({ limit: 50, roleCode: "CHEF" }),
  });

  const chefOptions = (chefs?.data ?? []).map((u) => ({
    id: Number(u.id),
    name: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email,
  }));

  const option = (type: "station" | "chef", id: number, label: string) => {
    const selected = value?.type === type && value.id === id;
    return (
      <Button
        key={`${type}-${id}`}
        variant="ghost"
        aria-pressed={selected}
        onClick={() => onChange(selected ? null : { type, id })}
        className={cn(
          "w-full justify-start px-2 py-2 h-auto text-sm",
          selected && "font-medium text-(--color-action)",
        )}
      >
        <span className="truncate">{label}</span>
      </Button>
    );
  };

  return (
    <>
      {stations && stations.length > 0 && (
        <>
          <div className="my-1 border-t border-(--color-border)" />
          <p className="px-2 pb-1 text-xs font-medium text-(--color-text-secondary) uppercase tracking-wide">{t`Station`}</p>
          {stations.map((s) => option("station", s.id, s.name))}
        </>
      )}
      {chefOptions.length > 0 && (
        <>
          <div className="my-1 border-t border-(--color-border)" />
          <p className="px-2 pb-1 text-xs font-medium text-(--color-text-secondary) uppercase tracking-wide">{t`Chef`}</p>
          {chefOptions.map((c) => option("chef", c.id, c.name))}
        </>
      )}
    </>
  );
}
